/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

/* Import from office code, as they donnot provide SDK for now */
/* ref: https://office.visualstudio.com/OC/_git/WordEdge?path=%2Fpackages%2Fbohemia-proxy%2Foffice-bohemia%2Fpackages%2Foffice-web-host%2Fsrc%2FIframe%2FShared%2FPostMessage.ts */
import { v1 } from 'uuid';

const messageTimeoutMs = 3000;

export const CommonErrorMessageType = 'error';

interface MinimalPostMessageContents {
    /** The contents of the message */
    payload?: any;

    /** If this message represents an error occurring */
    error?: true;
}

/** Format of Post Messages.  Based on Flux Standard Actions. */
export interface PostMessage<T extends string> extends MinimalPostMessageContents {
    /** Associates messages with a handler */
    type: T;
}

/** A reply message (when used in `PostMessageSender.sendReply`).
 * Same as a regular message, but doesn't contain the `type` field.
 */
export type PostMessageReply = MinimalPostMessageContents;

/** The internal shape of the Post Message contract, with metadata appended to track iframe instance and replies */
export interface PostMessageWithMetadata<T extends string> extends PostMessage<T> {
    /** Additional internal fields used by the postMessage library */
    meta?: {
        /** A unique message used to associate one message instance with another */
        messageId?: string;

        /** An identifier used to disambiguate between multiple frames sending messages to a single frame */
        senderId?: string;
    };
}

/** Set of functions used to send post messages */
export interface PostMessageSender {
    /** Sends the given message */
    send<T extends string>(message: PostMessage<T>): void;

    /**
     * Sends the given message but allows for a value to be returned
     *
     * @param message the message to send
     * @param timeout if we should timeout this message if we don't get a reply
     * within a time window
     * @returns a promise for the return value
     */
    sendWithReturn<T extends string>(message: PostMessage<T>, timeout?: boolean): Promise<PostMessageReply>;

    /**
     * Sends a message as a reply to another message.
     * Note: must be used in conjunction with a message that had been sent via `sendWithReturn`,
     * or else the reply will simply get lost.
     *
     * @param originalMessage the original message which was received
     * @param replyMessage the new message being sent as a reply
     */
    sendReply<T extends string>(originalMessage: PostMessage<T>, replyMessage: PostMessageReply): void;
}

/**
 * Creates functions used to send messages to the given target
 *
 * @param sourceWindow the window that is sending the messages
 * @param targetWindow the window to send the messages to
 * @param targetOrigin the origin associated with the window we're sending messages to
 * @param senderId Must be specified if message being sent from child frame
 */
export function createPostMessageSender(
    sourceWindow: Window,
    targetWindow: Window,
    targetOrigin: string,
    senderId?: string
): PostMessageSender {
    validateOrigin(targetOrigin);

    const sendInternal = (message: PostMessage<string>): void => {
        targetWindow?.postMessage(JSON.stringify(message), targetOrigin);
    };

    return {
        send(message: PostMessage<string>): void {
            const messageWithMeta = { ...message, meta: { senderId } };
            sendInternal(messageWithMeta);
        },
        sendWithReturn<T extends string, S extends string>(
            message: PostMessage<T>,
            timeout?: boolean
        ): Promise<PostMessage<S>> {
            const messageId = v1();
            let timeoutId: number | undefined;

            return new Promise<PostMessage<S>>(
                (resolve: (value: PostMessage<S>) => void, reject: (reason: string) => void): void => {
                    const listener = (event: MessageEvent): void => {
                        const message = messageEventToPostMessage<S>(event, targetOrigin);

                        if (message && message.meta && message.meta.messageId === messageId) {
                            sourceWindow.removeEventListener('message', listener);
                            resolve(message);
                        }
                    };

                    sourceWindow.addEventListener('message', listener);

                    const messageWithMeta: PostMessageWithMetadata<T> = { ...message, meta: { messageId, senderId } };
                    sendInternal(messageWithMeta);

                    if (timeout) {
                        timeoutId = sourceWindow.setTimeout((): void => {
                            reject('sendWithReply timeout');
                            timeoutId = undefined;
                        }, messageTimeoutMs);
                    }
                }
            ).then((msg: PostMessage<S>): PostMessage<S> => {
                if (timeoutId !== undefined) {
                    sourceWindow.clearTimeout(timeoutId);
                }
                return msg;
            });
        },
        sendReply<T extends string, S extends string>(
            originalMessage: PostMessageWithMetadata<T>,
            replyMessage: PostMessage<S>
        ): void {
            if (originalMessage.meta) {
                const messageWithMeta: PostMessageWithMetadata<S> = {
                    ...replyMessage,
                    meta: { messageId: originalMessage.meta.messageId, senderId },
                };
                sendInternal(messageWithMeta);
            }
        },
    };
}

export type PostMessageHandlers<T extends string> = {
    [postMessageType: string]: (message: PostMessage<T>) => void;
};

/**
 * Registers a set of post message handlers under the current window
 *
 * @param sourceWindow the window that is receiving the messages
 * @param senderOrigin what origin we expect to receive messages from
 * @param handlers the handlers for the different message types
 * @param senderId Must be set when receiving from child frame
 * @returns a callback for removing the listener from the current window
 */
export function setupPostMessageReceiver<T extends string>(
    sourceWindow: Window,
    senderOrigin: string,
    handlers: PostMessageHandlers<T>,
    senderId?: string
): { remove: () => void } {
    validateOrigin(senderOrigin);

    const listener = (event: MessageEvent<any>): void => {
        const message = messageEventToPostMessage<T>(event, senderOrigin);

        if (message) {
            if (senderId && (message.meta && message.meta.senderId) !== senderId) {
                return;
            }

            const handler = handlers[message.type];
            if (handler) {
                handler(message);
                // Try using common error handler
            } else if (message.error && handlers[CommonErrorMessageType]) {
                handlers[CommonErrorMessageType](message);
            }
        }
    };

    sourceWindow.addEventListener('message', listener, false);

    return { remove: (): void => sourceWindow.removeEventListener('message', listener) };
}

function messageEventToPostMessage<T extends string>(
    event: MessageEvent,
    expectedOrigin: string
): PostMessageWithMetadata<T> | undefined {
    if (event.origin !== expectedOrigin) {
        return undefined;
    }

    try {
        const deserializedMessage = JSON.parse(event.data);

        // If the message could not be deserialized or it does not have the right shape, ignore it.
        if (deserializedMessage && typeof deserializedMessage === 'object') {
            return deserializedMessage;
        }
    } catch {
        // Swallow exception
    }

    return undefined;
}

function validateOrigin(origin: string): void {
    // We do not allow the usage of '*' as an origin
    if (origin === '*') {
        throw new Error(`Post message origin invalid value: '${origin}'`);
    }
}

/**
 * Creates a set of post messages using the given sender (in case any of the
 * handlers need to send messages).
 */
export type PostMessageHandlerFactory = (sender: PostMessageSender) => PostMessageHandlers<string>;

// cspell:ignore deserialized
